import React, { useContext, useMemo } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from 'react-router-dom';
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from '@mui/material/Toolbar';
import Button from "@mui/material/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Typography from '@mui/material/Typography';
import { Store } from '../../store/Store';

import PageTemplate from '../../components/PageTemplate';
import ListingCard from '../../components/ListingCard';

import MapDrawer from '../../components/MapDrawer';
import { useApp } from '../../../context';
import { searchListings, describeSearch } from "../../../utils/search";
import { coordinatesFor } from "../../../data/locations";
import { toLatLng } from "../../../utils/geo";
import { activeServiceProvider } from "../../actions";

import './styles.css';

const drawerWidth = '40vw';

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    height: '100vh',
    overflow: 'hidden',
    overflowY: 'scroll',
    zIndex: 1,
    padding: theme.spacing(3),
    background: theme.palette.background.default,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    marginLeft: `-${drawerWidth}`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      }),
      marginLeft: 0
    })
  })
);

const transition = { duration: 0.3, ease: [0.43, 0.13, 0.23, 0.96] };

const frameVariants = {
  hover: { scale: 0.95 }
};

const Listing = ({ listing, onClick }) => (
  <motion.li
    variants={frameVariants}
    transition={transition}
    onClick={() => onClick(listing)}
  >
    <ListingCard listing={listing} />
  </motion.li>
);

/**
 * Search results, on the map.
 *
 * Reads its criteria FROM THE URL (`?q=` and `?location=`), so a result set is
 * linkable, refreshable and survives the Back button. This page used to read
 * its subject from `location.state` and filter the legacy `Store` — which had
 * two consequences: it was unreachable (nothing routed to it) and, had anything
 * routed to it, it would have shown the static seed data rather than the
 * Firestore listings every other screen renders.
 */
export default function MapListView() {
  const { dispatch } = useContext(Store);
  const [open, setOpen] = React.useState(true);
  const [searchParams] = useSearchParams();

  const { serviceListing, listingsLoaded } = useApp();

  const query = searchParams.get('q') ?? '';
  const place = searchParams.get('location') ?? '';
  // Set by the menu's category list. Exact taxonomy values, not free text.
  const type = searchParams.get('type') ?? '';
  const filter = searchParams.get('filter') ?? '';

  const results = useMemo(
    () => searchListings(serviceListing, { query, location: place, type, filter }),
    [serviceListing, query, place, type, filter]
  );

  /**
   * Where the map opens.
   *
   * The chosen destination if there is one, otherwise the first result — so a
   * free-text search across every location still opens somewhere relevant
   * rather than at the national default with the pins off screen.
   */
  const center = useMemo(() => {
    if (place) return { coordinates: coordinatesFor(place) };
    const firstPinned = results.find((listing) => toLatLng(listing));
    return firstPinned || null;
  }, [place, results]);

  const handleDrawerClose = () => setOpen(!open);

  const heading = describeSearch({ query, location: place, type, filter });

  return (
    <PageTemplate>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ position: "relative" }}>
          <MapDrawer
            open={open}
            handleDrawerClose={handleDrawerClose}
            providers={results}
            center={center}
          />
        </Box>
        <Main open={open}>
          <Toolbar />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              component={Link}
              to='/'
              className='btn-contain'
              variant="contained"
              startIcon={<ChevronLeftIcon />}
            >
              Back
            </Button>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }} noWrap>
                {heading}
              </Typography>
              {/* Counted only once the listener has delivered. "0 results" while
                  the snapshot is still in flight is a different statement from
                  "nothing matches", and the empty state below says so too. */}
              <Typography variant="body2" color="text.secondary">
                {listingsLoaded
                  ? `${results.length} ${results.length === 1 ? 'listing' : 'listings'}`
                  : 'Searching…'}
              </Typography>
            </Box>
          </Box>

          <motion.div
            initial="initial"
            animate="enter"
            exit="exit"
            variants={{ exit: { transition: { staggerChildren: 0.1 } } }}
          >
            <ul className="auto-grid">
              {results.map((listing, i) => (
                <Listing
                  key={`search-result-${listing.id || listing.name}-${i}`}
                  listing={listing}
                  i={i}
                  onClick={(provider) => activeServiceProvider(provider, dispatch)}
                />
              ))}
            </ul>
          </motion.div>

          {listingsLoaded && !results.length && (
            <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>
              Nothing matches {heading} yet. Try a different name, or widen the
              search to all locations.
            </Typography>
          )}
        </Main>
      </Box>
    </PageTemplate>
  );
}
